import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Row } from "react-bootstrap";
import { CategoryPickerCategory, CategoryPickerComponent } from "@me-pos/category-picker";
import i18n from "@me-team/host/src/i18n";
import { ErrorModalComponent } from "@me-pos/error-modal";
import i18next from "i18next";
import { CategoryInterface, TechMapsFilterI } from "../types";
import { useTranslation } from "react-i18next";
import { ApolloError } from "@apollo/client";
import { useGetTechMapsCategoriesQuery } from "@me-team/host/main/technologicalMaps/graphql/technologicalMaps.hooks";
import { normalizeCategoryData } from "../services/normalizeCategoryData";
import transformProductCategories from "@me-team/host/src/utils/transformProductCategories";
import ErrorService from "../../../services/ErrorService";
import { useParams } from "react-router-dom";

interface TechMapsFilterWrapperProps {
    setFilters: React.Dispatch<React.SetStateAction<TechMapsFilterI>>;
}

const TechMapsListForServiceFilterWrapper: React.FC<TechMapsFilterWrapperProps> = ({ setFilters }) => {
    const { t } = useTranslation();
    const dropdownRef = useRef(null);
    const { branchId } = useParams();
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<number>(null);
    const [search, setSearch] = useState("");
    const [selectedCategory, setSelectedCategory] = useState<CategoryInterface[]>(null);
    const [currentCatNameFilter, setCurrentCatNameFilter] = useState({
        categoryName: "",
        subCategoryName: "",
    });
    const [currentIdCatFilter, setCurrentIdCatFilter] = useState({
        categoryId: null,
        subCategoryId: null,
    });
    const [isPickerOpen, setIsPickerOpen] = useState(false);

    const { data: techMapsCategoriesData } = useGetTechMapsCategoriesQuery({
        variables: {
            page: 1,
            itemsPerPage: 1000,
            branchId: Number(branchId),
        },
        context: {
            errorType: "local",
        },
        onCompleted: (data) => {
            const normalizedItems = normalizeCategoryData(data);
            setSelectedCategory(transformProductCategories(normalizedItems));
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLInputElement;
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(target) &&
                target.id !== "technologicalMapsList-categoryId-field"
            ) {
                setIsPickerOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleCategoryNameChange = (categoryName: string) => {
        setCurrentCatNameFilter({ categoryName, subCategoryName: "" });
    };
    const handleSubCategoryNameChange = (subCategoryName: string, categoryName: string = "") => {
        setCurrentCatNameFilter({ categoryName, subCategoryName });
    };
    const handleCategoryIdChange = (categoryId: number) => {
        setCurrentIdCatFilter({ categoryId, subCategoryId: null });
    };
    const handleSubCategoryIdChange = (categoryId: number, subCategoryId: number) => {
        setCurrentIdCatFilter((prevState) => ({
            ...prevState,
            categoryId: categoryId,
            subCategoryId: subCategoryId,
        }));
    };

    const handleApplyFilter = () => {
        setFilters({ search: "", categoryId: null });
        setFilters({
            search: search ? search.trim() : "",
            categoryId: currentIdCatFilter?.subCategoryId
                ? currentIdCatFilter?.subCategoryId
                : currentIdCatFilter?.categoryId,
        });
        setIsPickerOpen(false);
    };

    const resetSelectedCategories = (categories: CategoryInterface[]) => {
        return categories?.map((category) => ({
            ...category,
            isPicked: false,
            isSubcategoryPiked: false,
            subcategoryList: category?.subcategoryList?.map((subcategory) => ({
                ...subcategory,
                isPicked: false,
            })),
        }));
    };

    const handleClearFilter = () => {
        setFilters({
            search: null,
            categoryId: null,
        });
        setCurrentCatNameFilter({ categoryName: "", subCategoryName: "" });
        setSearch("");
        setSelectedCategory(null);
        setCurrentIdCatFilter({ categoryId: null, subCategoryId: null });
        if (selectedCategory) {
            setSelectedCategory(resetSelectedCategories(selectedCategory));
        }

        setIsPickerOpen(false);
    };

    const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            handleApplyFilter();
        }
    };
    return (
        <>
            <Row className="gx-3">
                <Col lg={8} xxl={9}>
                    <Row className="gx-3 h-100">
                        <Col lg={6}>
                            <div className="position-relative mb-3 mb-lg-0">
                                <input
                                    name="search"
                                    id="technologicalMapsList-search-field"
                                    type="text"
                                    className="form-control pe-5 text-truncate"
                                    placeholder={t(
                                        "filterTechMapsForService.Search by the name of the technological map"
                                    )}
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value.trimStart())}
                                    onBlur={(e) => setSearch(e.target.value.trim())}
                                    onKeyDown={handleSearchKeyDown}
                                />
                                <i className="bi bi-search position-absolute top-50 end-0 translate-middle-y text-dark me-2"></i>
                            </div>
                        </Col>
                        <Col lg={6}>
                            <div className="position-relative mb-3 mb-lg-0">
                                <input
                                    type="text"
                                    id="technologicalMapsList-categoryId-field"
                                    className="form-control px-6 pe-5 cursor-pointer"
                                    placeholder={t("Category")}
                                    value={
                                        currentCatNameFilter?.categoryName || currentCatNameFilter?.subCategoryName
                                            ? `${currentCatNameFilter?.categoryName}  ${currentCatNameFilter?.subCategoryName}`
                                            : ""
                                    }
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setIsPickerOpen((prev) => !prev);
                                    }}
                                    readOnly
                                />
                                <i
                                    className={`bi bi-chevron-down position-absolute top-50 end-0 text-dark me-2 pointer-event-none transition-transform-icon ${isPickerOpen ? "rotate-180-icon" : ""}`}
                                ></i>
                                <div
                                    ref={dropdownRef}
                                    className="dropdown position-absolute opacity-1 z-2 top-10  start-0 end-0 mt-2 bg-white shadow"
                                >
                                    {techMapsCategoriesData && selectedCategory && (
                                        <CategoryPickerComponent
                                            i18n={i18next}
                                            isOpen={isPickerOpen}
                                            onSearchChange={(searchVealue: string) => {}}
                                            onChangeCategory={(currentCategory: CategoryPickerCategory) => {
                                                if (currentCategory.isPicked) {
                                                    handleCategoryNameChange(currentCategory?.name);
                                                    handleCategoryIdChange(currentCategory?.id);
                                                    setIsPickerOpen(false);
                                                } else {
                                                    setCurrentCatNameFilter({ categoryName: "", subCategoryName: "" });
                                                }
                                            }}
                                            onChangeSubcategory={(currentSubcategory, currentCategory) => {
                                                if (currentSubcategory?.isPicked) {
                                                    handleSubCategoryNameChange(currentSubcategory?.name);
                                                    handleSubCategoryIdChange(
                                                        currentCategory?.id,
                                                        currentSubcategory?.id
                                                    );
                                                    setIsPickerOpen(false);
                                                } else {
                                                    setCurrentCatNameFilter({ categoryName: "", subCategoryName: "" });
                                                }
                                            }}
                                            serviceCategories={selectedCategory}
                                        />
                                    )}
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Col>
                <Col lg={4} xxl={3}>
                    <Row className="gx-3 h-100">
                        <Col sm={6} className="mb-3 mb-lg-0">
                            <Button
                                type="button"
                                variant="outline-dark"
                                className="border-grey w-100 h-100 px-1 fw-normal"
                                onClick={handleClearFilter}
                            >
                                {t("clear")}
                            </Button>
                        </Col>
                        <Col sm={6} className="mb-3 mb-lg-0">
                            <Button
                                variant="primary"
                                className="w-100 h-100 px-1 fw-normal"
                                onClick={handleApplyFilter}
                            >
                                {t("apply")}
                            </Button>
                        </Col>
                    </Row>
                </Col>
            </Row>
            {isErrorModalOpen ? (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            ) : null}
        </>
    );
};

export default TechMapsListForServiceFilterWrapper;
